import { makeAutoObservable, autorun } from "mobx";
import { coerceUnknownFields } from "../data/BaseGame";
import RomData from "../data/RomData";
import { CONSTANTS } from "../config/Constants";

let _baseRom = null, _baseRomType = null;

class _GameData {
    userGames = [];
    currentGame = null;

    get currentGameId() {
        if (this.currentGame !== null) {
            return this.currentGame.id;
        } else { 
            return null;
        }
    }

    constructor() {
        this.reset()
        makeAutoObservable(this, {
        })
    }

    async _getRomCopy(emptyRom = false, smallRom = false) {
        if (_baseRomType !== smallRom ? 'small' : 'normal') {
            try {
                const fetchDat = await fetch(smallRom ? RomData.smallRomUrl : RomData.romUrl);
                const fetchData = new Uint8Array(await fetchDat.arrayBuffer());
                _baseRom = fetchData;
                _baseRomType = smallRom ? 'small' : 'normal';
            } catch (e) {
                console.error('Failed getting baserom for patching. Something is very wrong!!', e);
            }
        }
        if (emptyRom) {
            let emptyArray = []
            for (let i = 0; i < _baseRom.length; i++) {
                emptyArray.push(null);
            }

            return emptyArray;
        } else {
            return Uint8Array.from(_baseRom);
        }
    }

    addGame(gameData) {
        gameData.created = Date.now();
        gameData.updated = Date.now();
        this.userGames = [...this.userGames, gameData];
    }

    removeGame(id) {
        if (id === this.currentGameId) {
            this.currentGame = null;
        }
        this.userGames = this.userGames.filter(g => g.id !== id);
    }

    setCurrentGame(id) {
        this.currentGame = this.userGames.find(g => g.id === id);
    }
    
    replaceCurrentGame(newData) {
        this.currentGame = {...newData, id: this.currentGame.id, updated: Date.now()};
        this.userGames = this.userGames.map(g => g.id === this.currentGame.id ? this.currentGame : g);
    }
    // WARNING: I really mean reset. Be careful.
    reset() {
        this.userGames = []
        this.currentGame = null
    }

    findSmallRomIncompatibilities(gm) {
        if (gm.staticScreens.length > 8) {
            return 'Too many static screens';
        }
        if (gm.soundtrackData.length > RomData.patches.find(a => a.name === 'soundtrackData').smallRomLength) {
            return 'Famitracker music data is over 6kb';
        }
        return null;
    }

    async prepareGameForExport(originalGame) {
        let game = JSON.parse(JSON.stringify(coerceUnknownFields(originalGame)));

        // Patch in the default sound data
        if (game.soundtrackData.length === 0) {
            const data = (await import('../data/BuiltInSongs')).BuiltInMusicData
            game.soundtrackData = data;
        }
        return coerceUnknownFields(game);
    }

    async getCurrentPatchedRom(overrideLevel = false, overrideLevelData = null, sendRawData = false) {
        if (!this.currentGame) {
            throw new Error('No current game set, cannot patch rom');
        }
        return await this.getPatchedRom(this.currentGameId, overrideLevel, overrideLevelData, sendRawData);
    }

    async getPatchedRom(id, overrideLevel = null, overrideLevelData = null, sendRawData = false) {
        const game = this.userGames.find(g => g.id === id);
        if (game === null) {
            throw new Error('Game with id ' + id + ' not found!');
        }
        return this.getPatchedRomWithData(game, overrideLevel, overrideLevelData, sendRawData);
    }

    async getPatchedRomWithData(originalGame, overrideLevel = false, overrideLevelData = null, sendRawData = false) {

        const thisRom = await this._getRomCopy(sendRawData, !!originalGame.smallRom);

        // Step 1: validate we're looking at the right rom
        for (let i = 0; i < RomData.testMessageText.length; i++) {
            if (thisRom[RomData.testMessagePosition + i] !== RomData.testMessageText.charCodeAt(i) && thisRom[RomData.testMessagePosition + i] !== null) {
                throw new Error(`Rom does not match puzzle maker. Assuming something is wrong and exiting! (p: ${i}, v: ${thisRom[RomData.testMessagePosition + i]} v2: ${RomData.testMessageText.charCodeAt(i)})`);
            }
        }
        let game = await this.prepareGameForExport(originalGame)

        
        RomData.patches.forEach(patch => {
            let value = game[patch.name];
            if (patch.dataFormatter) {
                value = patch.dataFormatter(value, game);
            }

            if (patch.padBinary) {
                while (value.length < patch.length) { value.push(0); }
            }

            if (patch.padText) {
                while (value.length < patch.length) { value += ' ' }
            }

            const addr = game.smallRom ? patch.smallRomAddress : patch.address;
            if (patch.length !== false) {
                if (game.smallRom && patch.smallRomLength) {
                    for (let i = 0; i < patch.smallRomLength; i++) {
                        thisRom[addr + i] = (typeof value[i] === 'string' ? value.charCodeAt(i) : value[i]);
                    }
    
                } else {
                    for (let i = 0; i < patch.length; i++) {
                        thisRom[addr + i] = (typeof value[i] === 'string' ? value.charCodeAt(i) : value[i]);
                    }
                }
            } else {
                if (typeof value === 'boolean') {
                    thisRom[addr] = value ? 1 : 0
                } else if (typeof value === 'string') {
                    thisRom[addr] = value.charCodeAt(0);
                } else {
                    thisRom[addr] = value;
                }
            }
        });

        if (typeof overrideLevel === 'number') {
            const patch = RomData.patches.find(a => a.symbol === '_singleLevelOverride'),
                addr = game.smallRom ? patch.smallRomAddress : patch.address,
                lvlPatch = RomData.patches.find(a => a.symbol === '_gameLevelData'),
                lvlAddr = game.smallRom ? lvlPatch.smallRomAddress : lvlPatch.address;
            thisRom[addr] = overrideLevel;
            if (overrideLevelData) {
                // Manually override the data
                const processedLevel = lvlPatch.dataFormatter([overrideLevelData], game);
                for (let i = 0; i < 64; ++i) {
                    thisRom[lvlAddr + (overrideLevel*64) + i] = processedLevel[i];
                }
            }
        }
        if (sendRawData) {
            return thisRom;
        } else {
            return window.URL.createObjectURL(new Blob([thisRom], {type: "application/other"}));
        }
    }
    // Get all of the data for the current game in IPS format. Mesen can just kinda load this!
    // Not the most user friendly, but if you're doing this I wouldn't expect it to be!
    // Users can use it as-is, or patch their roms accordingly.
    async getCurrentRomIpsPatchData() {
        const rom = await this.getCurrentPatchedRom(false, null, true);

        let patches = [],
            inPatch = false,
            currAddress = 0,
            currData = [];
        for (let i = 0; i < rom.length; i++) {
            if (rom[i] === null) {
                if (inPatch) {
                    patches.push({address: currAddress, data: JSON.parse(JSON.stringify(currData))});
                }
                inPatch = false;
            } else {
                if (!inPatch) {
                    inPatch = true;
                    currAddress = i;
                    currData = [];
                }
                currData.push(rom[i]);
            }
        }
        
        let data = [];

        // Header
        [...'PATCH'].forEach(a => data.push(a.charCodeAt(0)));

        for (let i = 0; i < patches.length; i++) {
            // One big record for all data
            const pos = patches[i].address,
                start0 = pos & 0xff,
                start1 = (pos >> 8) & 0xff,
                start2 = (pos >> 16) & 0xff,
                size0 = (patches[i].data.length) & 0xff,
                size1 = (patches[i].data.length >> 8) & 0xff;

            data.push(start2, start1, start0, size1, size0);
            for (let j = 0; j < patches[i].data.length; j++) {
                data.push(patches[i].data[j]);
            }
        }

        [...'EOF'].forEach(a => data.push(a.charCodeAt(0)));
                
        return window.URL.createObjectURL(new Blob([Uint8Array.from(data)], {type: "application/other"}));
    }    

    async generatePatchableDataForCurrentRom() {
        if (!this.currentGame) {
            throw new Error('No current game set, cannot patch rom');
        }
        let game = coerceUnknownFields(this.currentGame);

        const patchChunks = [
`
; This is done in assembly so we can be a little more sure about where the linker will put this data.
; If it gets moved around, the application patcher will no longer work.

; NOTE: If you're editing the raw code of your own game you can ignore the warnings in this file, and move it wherever
; you want! Keep it in the same prg bank unless you know what you're doing though, that will break things. 
; These warnings are relating to keeping the web-based editor working.
${game.smallRom ? `
; SMALL ROM MODE IS ENABLED! You will not be able to build your rom by running \`create-nes-game build\` - you will
; need to provide special parameters. If you have make installed, use \`make build-compressed\`. If not, you can open
; the makefile in a text editor and copy the command directly under the line \`build-compressed:\`.
` : ''}

; Compatible rom version: ${CONSTANTS.GithubTag}
; smallRom mode: ${game.smallRom ? 'yes' : 'no'}
; https://github.com/cppchriscpp/nes-puzzle-maker-rom/releases/tag/${CONSTANTS.GithubTag}
; Created with nes puzzle maker v${CONSTANTS.Version}
;

.segment "USER_DATA"

; DO NOT REMOVE/EDIT! Used by engine to identify game and align patching
.asciiz "${RomData.testMessageText}" ; 0x12

`
        ];
        for (let i = 0; i < RomData.patches.length; i++) {
            const patch = RomData.patches[i];
            if (!patch.excludeSymbol) {
                let valueString = '    .byte 0';
                let value = game[patch.name];
                if (patch.dataFormatter) {
                    value = patch.dataFormatter(value, game);
                }
    
                if (patch.padBinary) {
                    let len = patch.length;
                    if (game.smallRom && patch.smallRomLength) {
                        len = patch.smallRomLength;
                    }
                    while (value.length < len) { value.push(0); }
                }
    
                if (patch.padText) {
                    let len = patch.length;
                    if (game.smallRom && patch.smallRomLength) {
                        len = patch.smallRomLength;
                    }
                    while (value.length < len) { value += ' ' }
                }

                if (typeof value === 'string') {
                    valueString = `    .asciiz "${value}"`
                } else {
    
                    if (typeof value.length !== 'undefined') {
                        // Build chunked array
                        const chunks = [];
                        for (let j = 0; j < value.length; j += 16) {
                            chunks.push(value.slice(j, j + 16));
                        }
                        valueString = '';
                        for (let j = 0; j < chunks.length; j++) {
                            valueString += `    .byte ${chunks[j].map(a => '$' + a.toString(16).padStart(2, '0')).join(', ')}\n`
                        }
                    } else {
                        if (typeof value === 'boolean') {
                            valueString = '    .byte ' + (value ? 1 : 0)
                        } else {
                            valueString = `    .byte ${value}`;
                        }
                    }
                }
                patchChunks.push(
`
${(patch.swapBank ? `.segment "${patch.swapBank}"\n` : '')
}_user${patch.symbol}:
${valueString}
.export _user${patch.symbol}: absolute
`
                )
            }
        }

        return patchChunks.join('\n')
    }
}

const GameData = new _GameData();
let prevGameData;
try {
    prevGameData = JSON.parse(localStorage.getItem('RetroPuzzleMakerV2.GameData'));
    Object.keys(prevGameData).forEach(key => {
        GameData[key] = prevGameData[key];
    });
} catch (e) {
    console.warn('Unable to load prior game data. This is expected on first load, but may otherwise indicate a problem.', e);
}
export default GameData;

autorun(() => {
    localStorage.setItem('RetroPuzzleMakerV2.GameData', JSON.stringify(GameData));
})

// Hack to make debugging easier. DO NOT EVER USE IN CODE
window.___GameData = GameData;


// NOTE: DO NOT REMOVE! This is listed in the documentation as a public api!
window.getIpsData = async function() { 
    const patch = await GameData.getCurrentRomIpsPatchData(),
        a = document.createElement('a');

    a.href = patch;
    a.download = 'puzzle.ips';
    a.click();

    setTimeout(() => {
        URL.revokeObjectURL(a.href)
    }, 5000);

    console.info('Copy the file you just saved into the same folder as `puzzle.nes` and your game data will be subbed in!')

};